<template>
  <div class="ticket-step">
    <!-- Loading State -->
    <div v-if="loading" class="ticket-step__state">
      <div class="ticket-step__spinner"></div>
      <p>Carregando ingressos disponíveis...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="ticket-step__state">
      <svg class="ticket-step__state-icon ticket-step__state-icon--error" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r=".5" fill="currentColor"/>
      </svg>
      <h4>Erro ao carregar ingressos</h4>
      <p class="ticket-step__state-msg">{{ error }}</p>
      <button type="button" class="ticket-step__retry" @click="loadAvailableTickets">
        Tentar novamente
      </button>
    </div>

    <!-- No Tickets -->
    <div v-else-if="availableTickets.length === 0 && soldOutTickets.length === 0" class="ticket-step__state">
      <svg class="ticket-step__state-icon" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
        <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2M13 17v2M13 11v2"/>
      </svg>
      <h4>Nenhum ingresso disponível</h4>
      <p class="ticket-step__state-msg">No momento não há ingressos disponíveis para este evento.</p>
    </div>

    <!-- ══════ SINGLE TICKET (V2 Hero) ══════ -->
    <template v-else-if="isSingleTicket">
      <div class="ticket-step__header">
        <h2 class="ticket-step__title">Garanta seu ingresso</h2>
        <p class="ticket-step__subtitle">Confira os detalhes antes de prosseguir</p>
      </div>

      <div class="v2-single">
        <!-- Corner check -->
        <div class="v2-corner" :style="{ background: buttonColor }">
          <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>
        </div>

        <div class="v2-top">
          <!-- Ticket icon -->
          <div class="v2-icon" :style="{ background: buttonColor }">
            <svg width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/>
              <path d="M13 5v2M13 17v2M13 11v2"/>
            </svg>
          </div>
          <div class="v2-info">
            <div class="v2-name">{{ singleTicket.name }}</div>
            <div class="v2-meta">
              <span v-if="singleTicket.description" class="v2-meta-item">
                {{ singleTicket.description }}
              </span>
              <span v-if="shouldShowSalesPeriod(singleTicket) && getSalesEndFormatted(singleTicket)" class="v2-meta-item">
                <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                Vendas até {{ getSalesEndFormatted(singleTicket) }}
              </span>
              <span v-if="shouldShowAvailability(singleTicket)" class="v2-meta-item">
                <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                {{ getAvailabilityText(singleTicket) }}
              </span>
              <span v-if="shouldShowMinMax(singleTicket) && singleTicket.max_per_order" class="v2-meta-item">
                Máx. {{ singleTicket.max_per_order }} por pedido
              </span>
            </div>
          </div>
        </div>

        <div class="v2-bottom">
          <div class="v2-price-block">
            <span class="v2-price-label">Valor</span>
            <span class="v2-price">{{ formatPriceShort(singleTicket.value) }}</span>
          </div>
          <div class="v2-qty">
            <button
              type="button"
              @click="decrementQuantity"
              :disabled="selectedQuantity <= (singleTicket.min_per_order || 1)"
            >−</button>
            <span class="qty-num">{{ selectedQuantity }}</span>
            <button
              type="button"
              @click="incrementQuantity"
              :disabled="selectedQuantity >= getMaxQuantity(singleTicket)"
            >+</button>
          </div>
        </div>
      </div>

      <!-- Validation message -->
      <div v-if="quantityValidationMessage" class="ticket-step__validation">
        <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r=".5" fill="currentColor"/></svg>
        {{ quantityValidationMessage }}
      </div>

      <!-- Continue button -->
      <button
        type="button"
        class="ticket-step__continue"
        :style="continueButtonStyle"
        :disabled="!canProceedToCheckout"
        @click="handleContinue"
        @mouseenter="continueHover = true"
        @mouseleave="continueHover = false"
      >
        <span class="ticket-step__continue-prefix">Continuar para pagamento ·</span>
        <span class="ticket-step__continue-amount">{{ formatPrice(selectedTicketTotal) }}</span>
      </button>
    </template>

    <!-- ══════ MULTI TICKET (V1 List) ══════ -->
    <template v-else>
      <div class="ticket-step__header">
        <h2 class="ticket-step__title">Escolha seu ingresso</h2>
        <p class="ticket-step__subtitle">Selecione o tipo e a quantidade desejada</p>
      </div>

      <div class="v1-list">
        <div
          v-for="ticket in availableTickets"
          :key="ticket.id"
          class="v1-ticket"
          :class="{ selected: selectedTicket?.id === ticket.id }"
          :style="selectedTicket?.id === ticket.id ? selectedCardStyle : {}"
          @click="handleTicketSelect(ticket)"
        >
          <!-- Radio -->
          <div class="v1-radio" :style="selectedTicket?.id === ticket.id ? { borderColor: buttonColor } : {}">
            <span v-if="selectedTicket?.id === ticket.id" class="v1-radio__dot" :style="{ background: buttonColor }"></span>
          </div>

          <!-- Info -->
          <div class="v1-info">
            <div class="v1-name-row">
              <span class="v1-name">{{ ticket.name }}</span>
              <span v-if="isLowStock(ticket)" class="v1-tag v1-tag--last">Últimas unidades</span>
            </div>
            <div class="v1-meta">
              <span v-if="ticket.description" class="v1-meta-item">{{ ticket.description }}</span>
              <span v-if="shouldShowAvailability(ticket)" class="v1-meta-item">
                {{ getAvailabilityText(ticket) }}
              </span>
              <span v-if="shouldShowMinMax(ticket) && ticket.max_per_order" class="v1-meta-item">
                Máx. {{ ticket.max_per_order }} por pedido
              </span>
              <span v-if="shouldShowSalesPeriod(ticket) && getSalesEndFormatted(ticket)" class="v1-meta-item">
                <svg width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                Até {{ getSalesEndFormatted(ticket) }}
              </span>
            </div>
          </div>

          <!-- Right: price + qty -->
          <div class="v1-right">
            <div class="v1-price">
              {{ formatPriceShort(ticket.value) }}
              <small>à vista</small>
            </div>
            <transition name="fade-qty">
              <div v-if="selectedTicket?.id === ticket.id" class="v1-qty" @click.stop>
                <button
                  type="button"
                  @click.stop="decrementQuantity"
                  :disabled="selectedQuantity <= (ticket.min_per_order || 1)"
                >−</button>
                <span class="qty-num">{{ selectedQuantity }}</span>
                <button
                  type="button"
                  @click.stop="incrementQuantity"
                  :disabled="selectedQuantity >= getMaxQuantity(ticket)"
                >+</button>
              </div>
            </transition>
          </div>
        </div>

        <!-- Sold-out tickets -->
        <div
          v-for="ticket in soldOutTickets"
          :key="'sold-' + ticket.id"
          class="v1-ticket v1-ticket--disabled"
        >
          <div class="v1-radio"></div>
          <div class="v1-info">
            <div class="v1-name-row">
              <span class="v1-name">{{ ticket.name }}</span>
              <span class="v1-tag v1-tag--soldout">Esgotado</span>
            </div>
            <div class="v1-meta">
              <span v-if="ticket.description" class="v1-meta-item">{{ ticket.description }}</span>
            </div>
          </div>
          <div class="v1-right">
            <div class="v1-price">
              {{ formatPriceShort(ticket.value) }}
              <small>à vista</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Validation message -->
      <div v-if="quantityValidationMessage && selectedTicket" class="ticket-step__validation">
        <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r=".5" fill="currentColor"/></svg>
        {{ quantityValidationMessage }}
      </div>

      <!-- Continue button -->
      <button
        type="button"
        class="ticket-step__continue"
        :style="continueButtonStyle"
        :disabled="!canProceedToCheckout"
        @click="handleContinue"
        @mouseenter="continueHover = true"
        @mouseleave="continueHover = false"
      >
        <span class="ticket-step__continue-prefix">Continuar ·</span>
        <span class="ticket-step__continue-amount">{{ formatPrice(selectedTicketTotal) }}</span>
      </button>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useTickets } from '../../composables/useTickets'

const props = defineProps({
  eventSlug: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['continue', 'ticket-selected'])

const {
  tickets,
  selectedTicket,
  selectedQuantity,
  loading,
  error,
  availableTickets,
  selectedTicketTotal,
  isQuantityValid,
  quantityValidationMessage,
  canProceedToCheckout,
  loadTickets,
  selectTicket,
  setQuantity,
  incrementQuantity: incrementQty,
  decrementQuantity: decrementQty,
  formatPrice,
  getSalesPeriod
} = useTickets()

const continueHover = ref(false)

// ── Computed ──

const buttonColor = computed(() => {
  if (typeof window !== 'undefined') {
    const c = getComputedStyle(document.documentElement).getPropertyValue('--button-color').trim()
    return c || '#4A6CF7'
  }
  return '#4A6CF7'
})

const buttonHoverColor = computed(() => {
  if (typeof window !== 'undefined') {
    const c = getComputedStyle(document.documentElement).getPropertyValue('--button-hover-color').trim()
    return c || '#3B5DE7'
  }
  return '#3B5DE7'
})

const isSingleTicket = computed(() => {
  return availableTickets.value.length === 1 && soldOutTickets.value.length === 0
})

const singleTicket = computed(() => {
  return availableTickets.value[0] || null
})

const soldOutTickets = computed(() => {
  return tickets.value.filter(t => {
    if (t.available_quantity !== null && t.available_quantity !== undefined) {
      return parseInt(t.available_quantity) <= 0
    }
    return false
  })
})

const selectedCardStyle = computed(() => ({
  borderColor: buttonColor.value,
  background: hexToRgba(buttonColor.value, 0.03),
  boxShadow: `0 0 0 3px ${hexToRgba(buttonColor.value, 0.06)}`
}))

const continueButtonStyle = computed(() => ({
  background: continueHover.value ? buttonHoverColor.value : buttonColor.value
}))

// ── Methods ──

const loadAvailableTickets = async () => {
  try {
    await loadTickets(props.eventSlug)
    // Auto-select if single ticket
    await nextTick()
    if (availableTickets.value.length === 1 && !selectedTicket.value) {
      autoSelectSingle()
    }
  } catch (err) {
    console.error('Error loading tickets:', err)
  }
}

const autoSelectSingle = () => {
  const ticket = availableTickets.value[0]
  if (ticket) {
    selectTicket(ticket)
    emit('ticket-selected', {
      ticket,
      quantity: selectedQuantity.value
    })
  }
}

onMounted(async () => {
  await loadAvailableTickets()
})

watch(() => props.eventSlug, async (newSlug, oldSlug) => {
  if (newSlug && newSlug !== oldSlug) {
    await loadAvailableTickets()
  }
})

const handleTicketSelect = (ticket) => {
  selectTicket(ticket)
  emit('ticket-selected', {
    ticket,
    quantity: selectedQuantity.value
  })
}

const incrementQuantity = () => {
  incrementQty()
  if (selectedTicket.value) {
    emit('ticket-selected', {
      ticket: selectedTicket.value,
      quantity: selectedQuantity.value
    })
  }
}

const decrementQuantity = () => {
  decrementQty()
  if (selectedTicket.value) {
    emit('ticket-selected', {
      ticket: selectedTicket.value,
      quantity: selectedQuantity.value
    })
  }
}

const getMaxQuantity = (ticket) => {
  const limits = []
  if (ticket.max_per_order) limits.push(parseInt(ticket.max_per_order))
  if (ticket.available_quantity !== null && ticket.available_quantity !== undefined) {
    limits.push(parseInt(ticket.available_quantity))
  }
  return limits.length > 0 ? Math.min(...limits) : 999
}

const getAvailabilityText = (ticket) => {
  if (ticket.available_quantity === null || ticket.available_quantity === undefined) {
    return 'Disponível'
  }
  const available = parseInt(ticket.available_quantity)
  if (available <= 0) return 'Esgotado'
  if (available <= 10) return `Últimas ${available} unidades`
  return `${available} disponíveis`
}

const isLowStock = (ticket) => {
  if (ticket.available_quantity === null || ticket.available_quantity === undefined) return false
  const available = parseInt(ticket.available_quantity)
  return available > 0 && available <= 10
}

const getSalesEndFormatted = (ticket) => {
  if (!ticket.sales_end) return null
  const date = new Date(ticket.sales_end)
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const formatPriceShort = (value) => {
  if (!value && value !== 0) return 'Gratuito'
  const num = parseFloat(value)
  if (num === 0) return 'Gratuito'
  // Format: R$ 597
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(num)
}

// Visibility helpers — respect backend flags, default to showing if flag is absent
const shouldShowAvailability = (ticket) => {
  if (ticket.show_available_quantity !== undefined) return !!ticket.show_available_quantity
  return true
}

const shouldShowSalesPeriod = (ticket) => {
  if (ticket.show_sales_period !== undefined) return !!ticket.show_sales_period
  return true
}

const shouldShowMinMax = (ticket) => {
  if (ticket.show_min_max_per_order !== undefined) return !!ticket.show_min_max_per_order
  return true
}

const hexToRgba = (hex, alpha) => {
  if (!hex) return `rgba(74,108,247,${alpha})`
  const h = hex.replace('#', '')
  const r = parseInt(h.substring(0, 2), 16) || 74
  const g = parseInt(h.substring(2, 4), 16) || 108
  const b = parseInt(h.substring(4, 6), 16) || 247
  return `rgba(${r},${g},${b},${alpha})`
}

const handleContinue = () => {
  if (canProceedToCheckout.value) {
    emit('continue', {
      ticket: selectedTicket.value,
      quantity: selectedQuantity.value,
      total: selectedTicketTotal.value
    })
  }
}
</script>

<style scoped>
/* ── Base ── */
.ticket-step {
  padding: 28px 0;
}

.ticket-step__header {
  text-align: center;
  margin-bottom: 24px;
}

.ticket-step__title {
  font-size: 22px;
  font-weight: 500;
  color: #1B2A4A;
  margin: 0 0 6px 0;
}

.ticket-step__subtitle {
  font-size: 13px;
  color: #7A8BA7;
  margin: 0;
}

/* ── States (loading, error, empty) ── */
.ticket-step__state {
  text-align: center;
  padding: 60px 20px;
}

.ticket-step__spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 16px;
  border: 3px solid #E3E8F0;
  border-top-color: var(--button-color, #4A6CF7);
  border-radius: 50%;
  animation: spin .8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.ticket-step__state-icon {
  color: #C4CFDE;
  margin-bottom: 12px;
}

.ticket-step__state-icon--error {
  color: #EF4444;
}

.ticket-step__state h4 {
  font-size: 18px;
  font-weight: 700;
  color: #1B2A4A;
  margin: 0 0 6px 0;
}

.ticket-step__state-msg {
  font-size: 13px;
  color: #7A8BA7;
  margin: 0 0 20px 0;
}

.ticket-step__retry {
  padding: 10px 24px;
  background: var(--button-color, #4A6CF7);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s;
}

.ticket-step__retry:hover {
  background: var(--button-hover-color, #3B5DE7);
}

/* ── Validation message ── */
.ticket-step__validation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  margin-top: 12px;
  background: #FFFBEB;
  border-radius: 8px;
  color: #92400E;
  font-size: 12px;
  font-weight: 500;
}

/* ── Continue button ── */
.ticket-step__continue {
  width: 100%;
  padding: 14px;
  margin-top: 20px;
  border: none;
  border-radius: 10px;
  background: var(--button-color, #4A6CF7);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all .15s cubic-bezier(.4,0,.2,1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.ticket-step__continue:hover:not(:disabled) {
  background: var(--button-hover-color, #3B5DE7);
}

.ticket-step__continue:disabled {
  opacity: .45;
  cursor: not-allowed;
}

.ticket-step__continue-prefix {
  opacity: .7;
  font-weight: 500;
}

.ticket-step__continue-amount {
  font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', 'Consolas', monospace;
}

/* ═══════════════════════════════════════════
   V1 — MULTI-TICKET LIST
   ═══════════════════════════════════════════ */

.v1-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.v1-ticket {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border: 1.5px solid #E3E8F0;
  border-radius: 14px;
  cursor: pointer;
  transition: all .15s cubic-bezier(.4,0,.2,1);
  background: #fff;
}

.v1-ticket:hover:not(.v1-ticket--disabled) {
  border-color: rgba(var(--button-color-rgb, 74,108,247), 0.3);
  background: rgba(var(--button-color-rgb, 74,108,247), 0.02);
}

.v1-ticket.selected {
  /* Dynamic styles applied via :style binding */
}

.v1-ticket--disabled {
  opacity: .55;
  cursor: not-allowed;
  background: #F0F2F6;
}

/* Radio */
.v1-radio {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #C4CFDE;
  flex-shrink: 0;
  margin-right: 14px;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all .15s;
}

.v1-radio__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: block;
}

/* Info */
.v1-info {
  flex: 1;
  min-width: 0;
}

.v1-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}

.v1-name {
  font-size: 15px;
  font-weight: 700;
  color: #1B2A4A;
}

.v1-tag {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .5px;
  padding: 2px 7px;
  border-radius: 4px;
}

.v1-tag--last {
  background: #FFFBEB;
  color: #F59E0B;
}

.v1-tag--soldout {
  background: #FEE2E2;
  color: #EF4444;
}

.v1-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  font-size: 11.5px;
  color: #7A8BA7;
}

.v1-meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.v1-meta-item svg {
  width: 11px;
  height: 11px;
  flex-shrink: 0;
}

/* Right side */
.v1-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  margin-left: 16px;
  flex-shrink: 0;
}

.v1-price {
  font-size: 18px;
  font-weight: 800;
  color: #1B2A4A;
  font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', 'Consolas', monospace;
  white-space: nowrap;
  line-height: 1;
}

.v1-price small {
  font-size: 10px;
  font-weight: 600;
  color: #7A8BA7;
  display: block;
  text-align: right;
  font-family: inherit;
  margin-top: 3px;
}

.v1-qty {
  display: flex;
  align-items: center;
  border: 1px solid #E3E8F0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.v1-qty button {
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7A8BA7;
  font-family: inherit;
  font-size: 14px;
  transition: all .1s;
}

.v1-qty button:hover:not(:disabled) {
  background: #F0F2F6;
  color: #1B2A4A;
}

.v1-qty button:disabled {
  opacity: .3;
  cursor: not-allowed;
}

.v1-qty .qty-num {
  min-width: 30px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: #1B2A4A;
  font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', 'Consolas', monospace;
}

/* Qty transition */
.fade-qty-enter-active {
  transition: all .2s ease-out;
}
.fade-qty-leave-active {
  transition: all .15s ease-in;
}
.fade-qty-enter-from,
.fade-qty-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ═══════════════════════════════════════════
   V2 — SINGLE TICKET HERO
   ═══════════════════════════════════════════ */

.v2-single {
  border: 1.5px solid var(--button-color, #4A6CF7);
  background: rgba(var(--button-color-rgb, 74,108,247), 0.02);
  border-radius: 14px;
  padding: 20px;
  position: relative;
}

.v2-corner {
  position: absolute;
  top: 0;
  right: 0;
  width: 32px;
  height: 32px;
  border-radius: 0 14px 0 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.v2-top {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 18px;
  padding-right: 36px;
}

.v2-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.v2-info {
  flex: 1;
}

.v2-name {
  font-size: 18px;
  font-weight: 800;
  color: #1B2A4A;
  margin-bottom: 6px;
}

.v2-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  font-size: 12px;
  color: #7A8BA7;
}

.v2-meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.v2-meta-item svg {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

.v2-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid rgba(var(--button-color-rgb, 74,108,247), 0.12);
}

.v2-price-block {
  display: flex;
  flex-direction: column;
}

.v2-price-label {
  font-size: 10px;
  color: #7A8BA7;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .5px;
  margin-bottom: 2px;
}

.v2-price {
  font-size: 28px;
  font-weight: 800;
  color: #1B2A4A;
  font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', 'Consolas', monospace;
  line-height: 1;
}

.v2-qty {
  display: flex;
  align-items: center;
  border: 1px solid #E3E8F0;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

.v2-qty button {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3D4F6F;
  font-family: inherit;
  font-size: 16px;
  transition: background .1s;
}

.v2-qty button:hover:not(:disabled) {
  background: #F0F2F6;
}

.v2-qty button:disabled {
  opacity: .3;
  cursor: not-allowed;
}

.v2-qty .qty-num {
  min-width: 40px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: #1B2A4A;
  font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', 'Consolas', monospace;
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .ticket-step {
    padding: 20px 0;
  }

  .ticket-step__title {
    font-size: 20px;
  }

  .v1-ticket {
    padding: 14px;
    flex-wrap: wrap;
  }

  .v1-right {
    margin-left: 0;
    margin-top: 12px;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding-left: 32px;
  }

  .v1-price small {
    display: inline;
    text-align: left;
    margin-top: 0;
    margin-left: 4px;
  }

  .v2-top {
    flex-direction: column;
    gap: 12px;
    padding-right: 36px;
  }

  .v2-price {
    font-size: 22px;
  }

  .v2-bottom {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }

  .v2-qty {
    align-self: stretch;
    justify-content: center;
  }
}
</style>
